import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { X, Wand, UserRoundSearch, Mail, Split, UserCheck, Clock, Play, Pen, Search, Filter } from 'lucide-react';
import { hasValidationErrors } from './config/nodeValidationUtils';
import { useWorkflowSchema } from '../../../hooks/workflows/useWorkflowSchema';

interface WorkflowNodeProps {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
  selected: boolean;
  onSelect: () => void;
  onDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onDragEnd?: () => void;
  onDelete: () => void;
  onAddAfter: () => void;
  disableDelete: boolean;
  disabled?: boolean;
  hasIncomingEdge?: boolean;
  hasOutgoingEdge?: boolean;
}

const NODE_ICONS = {
  FetchLeadsActivity: <Search className="w-5 h-5" />,
  LeadFilterActivity: <Filter className="w-5 h-5" />,
  EmailEnrichmentActivity: <UserRoundSearch className="w-5 h-5" />,
  PersonalizedMessageActivity: <Wand className="w-5 h-5" />,
  EmailSendingActivity: <Mail className="w-5 h-5" />,
  LinkedInEnrichmentActivity: <UserCheck className="w-5 h-5" />,
  EmailReplyWaitActivity: <Clock className="w-5 h-5" />,
  EmailReplyClassificationActivity: <Split className="w-5 h-5" />,
  default: <Play className="w-5 h-5" />
};

const NODE_COLORS = {
  FetchLeadsActivity: 'bg-green-500 border-green-600',
  LeadFilterActivity: 'bg-gray-500 border-gray-600',
  EmailEnrichmentActivity: 'bg-blue-500 border-blue-600',
  PersonalizedMessageActivity: 'bg-orange-500 border-orange-600',
  EmailSendingActivity: 'bg-purple-500 border-purple-600',
  LinkedInEnrichmentActivity: 'bg-purple-500 border-purple-600',
  EmailReplyWaitActivity: 'bg-yellow-500 border-yellow-600',
  EmailReplyClassificationActivity: 'bg-red-500 border-red-600',
  default: 'bg-gray-500 border-gray-600'
};

const WorkflowNodeComponent: React.FC<WorkflowNodeProps> = ({
  id,
  type,
  position,
  data,
  selected,
  onSelect,
  onDrag,
  onDragEnd,
  onDelete,
  disableDelete,
  disabled = false,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Use refs to avoid stale closures in drag handlers
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    dragOffsetRef.current = dragOffset;
  }, [dragOffset]);

  const { parameterTemplates } = useWorkflowSchema();
  const template = parameterTemplates[type] || {};

  const icon = NODE_ICONS[type as keyof typeof NODE_ICONS] || NODE_ICONS.default;
  const colorClass = NODE_COLORS[type as keyof typeof NODE_COLORS] || NODE_COLORS.default;
  const isStaticNode = ['StartNode'].includes(type);
  const hasErrors = !isStaticNode && hasValidationErrors(data.config, template);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || isStaticNode) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get the canvas element to calculate positions relative to it
    const canvas = nodeRef.current?.closest('.workflow-canvas');
    const canvasRect = canvas?.getBoundingClientRect();
    const nodeRect = nodeRef.current?.getBoundingClientRect();
    
    if (canvasRect && nodeRect) {
      setDragOffset({
        x: e.clientX - nodeRect.left,
        y: e.clientY - nodeRect.top
      });
      setIsDragging(true);
      onSelect();
    }
  };

  // Stable event handlers that use refs to avoid recreating on every render
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || disabled || isStaticNode || !e) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = nodeRef.current?.closest('.workflow-canvas');
    const canvasRect = canvas?.getBoundingClientRect();
    
    if (canvasRect) {
      // Calculate new position relative to canvas, accounting for zoom and pan
      const newPosition = {
        x: e.clientX - canvasRect.left - dragOffsetRef.current.x,
        y: e.clientY - canvasRect.top - dragOffsetRef.current.y
      };
      onDrag(id, newPosition);
    }
  }, [disabled, id, onDrag, isStaticNode]);

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [onDragEnd]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <>
      <div
        id={`workflow-node-${id}`}
        ref={nodeRef}
        className={`workflow-node w-[15vw] rounded-lg shadow-md absolute select-none transition-all duration-200 ${
          selected ? 'z-20' : 'z-10'
        }`}
        style={{
          left: position.x,
          top: position.y,
          transform: isDragging ? 'scale(1.05)' : 'scale(1)'
        }}
        onMouseDown={handleMouseDown}
        role="button"
        tabIndex={0}
        aria-label={`${type} node: ${data.name || type.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim()}`}
        aria-selected={selected}
        aria-describedby={hasErrors ? `node-errors-${id}` : undefined}
      >
        {/* Node Body */}
        {isStaticNode ? (
          /* Static Nodes */
          <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 w-full">
            <div className="text-sm text-gray-500 text-center">Start</div>
          </div>
        ) : (
          /* Regular Action Node */
          <div className={`w-full cursor-move bg-white border border-gray-200 rounded-lg transition-all duration-200 ${
            hasErrors 
              ? 'border-red-500 shadow-md' 
              : selected 
                ? 'border-blue-500 shadow-md' 
                : 'border-gray-200 hover:border-gray-300'
          }`}>
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`p-2 rounded-full flex items-center justify-center mr-3 ${colorClass.replace('border-', 'bg-').replace('-600', '-100')}`}>
                    <div className={`text-white ${colorClass.split(' ')[0]}`}>
                      {icon}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {data.name || type.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
                <Pen className="cursor-pointer w-3 h-3 text-gray-400 hover:text-gray-600 cursor-pointer" />
              </div>
            </div>
            {(selected || hasErrors) && (
              <div className="px-4 pb-2">
                <div 
                  id={hasErrors ? `node-errors-${id}` : undefined}
                  className={`text-xs px-2 py-1 rounded ${
                    hasErrors 
                      ? 'text-red-600 bg-red-50' 
                      : 'text-amber-600 bg-amber-50'
                  }`}
                  role={hasErrors ? 'alert' : 'status'}
                >
                  {hasErrors ? 'Required fields missing' : 'Edit Configuration in the right panel'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {!disableDelete && selected && !disabled && (
          <div className="absolute -top-2 -right-2">
            <button
              id={`delete-node-${id}`}
              onClick={handleDelete}
              className="w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-xs transition-colors"
              title="Delete node"
              aria-label={`Delete ${type} node`}
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export const WorkflowNode = memo(WorkflowNodeComponent, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.type === nextProps.type &&
    prevProps.position.x === nextProps.position.x &&
    prevProps.position.y === nextProps.position.y &&
    prevProps.selected === nextProps.selected &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.disableDelete === nextProps.disableDelete &&
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data)
  );
});